import { useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { Search, Download, Euro, ShoppingCart, User, Mail } from 'lucide-react';
import { formatDate } from '../lib/formatDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Tab "Venduto" del Magazzino: archivio storico di tutto ciò che è stato venduto.
 * Ricerca per prodotto/matricola/cliente + filtro data + riepilogo + export Excel.
 */
export default function VendutoTab({ articoli, isSuperAdmin }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [dataDa, setDataDa] = useState('');
  const [dataA, setDataA] = useState('');
  const [exporting, setExporting] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  const handleInviaReport = async () => {
    setSendingReport(true);
    try {
      const { data } = await axios.post(`${API}/magazzino/report-venduto/invia`);
      toast.success(`📊 Report ${data.mese_label} inviato a ${data.destinatario} (${data.pezzi} pezzi, ${data.totale.toFixed(2)} €)`);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Errore invio report';
      toast.error(msg, { duration: 8000 });
    } finally {
      setSendingReport(false);
    }
  };

  const venduti = useMemo(() => {
    const q = search.toLowerCase();
    return articoli
      .filter((a) => a.stato === 'venduto')
      .filter((a) => {
        if (q) {
          const match = [a.descrizione, a.marca, a.modello, a.matricola, a.codice, a.codice_barre, a.venduto_cliente_nome]
            .some((f) => (f || '').toLowerCase().includes(q));
          if (!match) return false;
        }
        const dataVendita = (a.data_uscita || '').substring(0, 10);
        if (dataDa && dataVendita && dataVendita < dataDa) return false;
        if (dataA && dataVendita && dataVendita > dataA) return false;
        return true;
      })
      .sort((a, b) => (b.data_uscita || '').localeCompare(a.data_uscita || ''));
  }, [articoli, search, dataDa, dataA]);

  const totale = venduti.reduce((s, a) => s + (a.prezzo_vendita || 0), 0);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (dataDa) params.set('data_da', dataDa);
      if (dataA) params.set('data_a', dataA);
      const response = await axios.get(`${API}/export/venduto?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'venduto_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('✅ Export venduto scaricato');
    } catch (error) {
      toast.error('Errore export');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Riepilogo */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="rounded-none border-l-4 border-l-emerald-500">
          <CardContent className="pt-4 flex items-center gap-3">
            <ShoppingCart className="h-6 w-6 text-emerald-600" />
            <div>
              <div className="text-2xl font-black" data-testid="venduto-count">{venduti.length}</div>
              <div className="text-xs text-muted-foreground uppercase font-mono">Pezzi venduti</div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-none border-l-4 border-l-cyan-500">
          <CardContent className="pt-4 flex items-center gap-3">
            <Euro className="h-6 w-6 text-cyan-600" />
            <div>
              <div className="text-2xl font-black font-mono" data-testid="venduto-totale">{totale.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
              <div className="text-xs text-muted-foreground uppercase font-mono">Valore venduto</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <div className="flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca prodotto, matricola o cliente..."
            className="rounded-none pl-9"
            data-testid="venduto-search"
          />
        </div>
        <div className="flex gap-2 items-center">
          <Input type="date" value={dataDa} onChange={(e) => setDataDa(e.target.value)} className="rounded-none w-auto" title="Da data" data-testid="venduto-data-da" />
          <span className="text-muted-foreground text-xs">→</span>
          <Input type="date" value={dataA} onChange={(e) => setDataA(e.target.value)} className="rounded-none w-auto" title="A data" data-testid="venduto-data-a" />
          <Button variant="outline" className="rounded-none" onClick={handleExport} disabled={exporting} data-testid="venduto-export-btn">
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Esporto...' : 'Export Excel'}
          </Button>
          <Button variant="outline" className="rounded-none" onClick={handleInviaReport} disabled={sendingReport} title="Invia subito via email il report del mese corrente (automatico ogni 1° del mese)" data-testid="venduto-report-btn">
            <Mail className="h-4 w-4 mr-2" />
            {sendingReport ? 'Invio...' : 'Report Email'}
          </Button>
        </div>
      </div>

      {/* Tabella */}
      <div className="border border-border overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Data Vendita</th>
              <th>Prodotto</th>
              <th className="hidden md:table-cell">Matricola</th>
              <th>Cliente</th>
              <th className="hidden lg:table-cell">Prezzo</th>
              <th className="hidden lg:table-cell">Venduto da</th>
            </tr>
          </thead>
          <tbody>
            {venduti.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted-foreground py-8">
                  Nessuna vendita trovata
                </td>
              </tr>
            ) : (
              venduti.map((a) => (
                <tr key={a.id} data-testid={`venduto-row-${a.id}`}>
                  <td className="font-mono text-xs font-semibold">{formatDate(a.data_uscita)}</td>
                  <td>
                    <div className="font-medium">{a.descrizione}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">
                      {[a.marca, a.modello].filter(Boolean).join(' · ')}{a.codice ? ` — ${a.codice}` : ''}
                    </div>
                  </td>
                  <td className="hidden md:table-cell font-mono text-xs">{a.matricola || '-'}</td>
                  <td>
                    {a.venduto_cliente_id ? (
                      <button
                        onClick={() => navigate(`/clienti/${a.venduto_cliente_id}`)}
                        className="flex items-center gap-1 text-cyan-600 dark:text-cyan-400 hover:underline text-left"
                        data-testid={`venduto-cliente-${a.id}`}
                      >
                        <User className="h-3 w-3 shrink-0" />
                        {a.venduto_cliente_nome || 'Cliente'}
                      </button>
                    ) : (
                      <span className="text-muted-foreground">{a.venduto_cliente_nome || '-'}</span>
                    )}
                  </td>
                  <td className="hidden lg:table-cell font-mono font-semibold">{(a.prezzo_vendita || 0).toFixed(2)} €</td>
                  <td className="hidden lg:table-cell text-xs text-muted-foreground">{a.venduto_da_utente || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
